import { useI18n } from '../composables/i18n.composable';
import { DCPValueUnitDTO } from '@dh-io-emh/commerce-api/src/models/shared/ocb/api.schema';
import { getTranslationUnitFallback } from './translation.utils';

const POWER_LABEL_ELECTRIC_KEY = 'dcp.powerElectricLabel';
const POWER_LABEL_HYBRID_KEY = 'dcp.powerHybridLabel';

/**
 * Retrieves a power value and uses the getTranslationUnitFallback to
 * check for alternative unit translations. Returns the value with the unit.
 * @param power The power value (either combustion or electric)
 * @returns A string containing the power value with unit
 */
const valueAndUnit = (power: DCPValueUnitDTO): string => {
  return getTranslationUnitFallback(
    power.value as number,
    `dcp.unit.${power.unitCode}`,
    `${power.value} ${power.unit}`,
  );
};

/**
 * Retrieves combustion and electric power values and returns a string containing
 * either the combustion power or the electric power or the combination of the two
 * with the given unit.
 * @param combustionPower Combustion power value
 * @param electricPower Electric power value
 * @param usePowerLabel Usage of power label
 * @returns Returns a string containing the power value with unit.
 * In case of hybrid the string will contain two (combined) values
 */
const getCombinedValue = (
  combustionPower: DCPValueUnitDTO,
  electricPower: DCPValueUnitDTO,
  usePowerLabel?: boolean,
): string => {
  const { t, exists } = useI18n();

  // If combustion power only
  if (combustionPower.value && !electricPower.value) {
    return valueAndUnit(combustionPower);
  }

  // If hybrid power (combustion + electric)
  if (combustionPower.value && electricPower.value) {
    // If power label hybrid should be used
    if (usePowerLabel && exists(POWER_LABEL_HYBRID_KEY)) {
      return `${valueAndUnit(combustionPower)} + ${t(POWER_LABEL_HYBRID_KEY, {
        power: valueAndUnit(electricPower),
      })}`;
    }

    return `${valueAndUnit(combustionPower)} + ${valueAndUnit(electricPower)}`;
  }

  // If electric power only
  if (!combustionPower.value && electricPower.value) {
    // If power label electric should be used
    if (usePowerLabel && exists(POWER_LABEL_ELECTRIC_KEY)) {
      return t(POWER_LABEL_ELECTRIC_KEY, {
        power: valueAndUnit(electricPower),
      });
    }

    return valueAndUnit(electricPower);
  }

  // Return empty string if no values are given
  return '';
};

/**
 * Helper function to return a formatted engine description for Vehicle model
 * @param combustionPower Primary combustion power value
 * @param combustionPowerSecondary Secondary combustion power value
 * @param electricPower Primary electric power value
 * @param electricPowerSecondary Secondary electric power value
 * @returns Object containing primary and secondary engine description
 */
export const getEngineDescription = (
  combustionPower: DCPValueUnitDTO,
  combustionPowerSecondary: DCPValueUnitDTO,
  electricPower: DCPValueUnitDTO,
  electricPowerSecondary: DCPValueUnitDTO,
): string => {
  // Use power label only for primary value
  // TODO: Check case for using power label when only secondary power value are given.
  const primaryValue = getCombinedValue(combustionPower, electricPower, true);
  const secondaryValue = getCombinedValue(combustionPowerSecondary, electricPowerSecondary);

  if (primaryValue) {
    return secondaryValue ? `${primaryValue} (${secondaryValue})` : primaryValue;
  } else {
    return secondaryValue;
  }
};
